import React, { ChangeEvent, useCallback, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  GetPossiblePublicCommunitiesDocument,
  GetRestrictedCompanySettingsDocument,
  SetAllowedCommunitiesDocument,
} from '~/api/generated/graphql'
import { useCommunity } from '~/contexts/CommunityContext'
import { useAuth } from '~/auth/Auth'
import { Button } from 'react-bootstrap'
import { useNavigate } from 'react-router'

type CommunityRow = {
  communityId: string
  name: string
}

type CommunityAllowListProps = {
  communities: CommunityRow[]
  initialSelectedIds: string[]
  name: string
  companyId: string
}

const CommunityAllowList = ({ communities, initialSelectedIds, name, companyId }: CommunityAllowListProps) => {
  const { actingSysAdmin } = useAuth()
  const cancelBtnRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()

  const returnHome = useCallback(() => {
    void navigate(`../home`)
  }, [navigate])

  const [selectedCommunities, setSelectedCommunities] = useState<string[]>(initialSelectedIds)
  const noChanges =
    selectedCommunities.length == initialSelectedIds.length &&
    selectedCommunities.every(communityId => initialSelectedIds.includes(communityId))

  const handleCancel = () => {
    returnHome()
  }

  const [setAllowedCompanies] = useMutation(SetAllowedCommunitiesDocument)

  const handleSave = async () => {
    if (noChanges) {
      returnHome()
    } else {
      const resp = await setAllowedCompanies({
        variables: { companyId: companyId, communityIds: selectedCommunities },
      })
      if (resp.data?.setAllowedCommunities?.ok) {
        returnHome()
      }
    }
  }

  const isChecked = (communityId: string) => {
    return selectedCommunities?.includes(communityId)
  }

  const checkChanged = (event: ChangeEvent<HTMLInputElement>, communityId: string) => {
    if (event.target.checked) {
      setSelectedCommunities(communities => {
        const communitiesToUpdate = new Set(communities)
        communitiesToUpdate.add(communityId)
        return [...communitiesToUpdate]
      })
    } else {
      setSelectedCommunities(communities => {
        const communitiesToUpdate = new Set(communities)
        communitiesToUpdate.delete(communityId)
        return [...communitiesToUpdate]
      })
    }
  }

  return (
    <>
      <div className={'settings-container'}>
        <div className="top-items">
          <h3 className="comm-title">{`${name} Settings`}</h3>
          <span className={'button-options'}>
            <Button variant="light" onClick={handleCancel} ref={cancelBtnRef}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!actingSysAdmin} onClick={handleSave}>
              Save
            </Button>
          </span>
        </div>
        <div className={'allow-list-section'}>
          <h3 className="section-title">Authorized Public Communities</h3>
          {communities.map(community => {
            const communityId = community.communityId
            return (
              <div key={communityId}>
                <input
                  type={'checkbox'}
                  checked={isChecked(communityId ?? '')}
                  disabled={!actingSysAdmin}
                  onChange={event => checkChanged(event, communityId)}
                />
                <span>{community.name}</span>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export const RestrictedSiteSettings = () => {
  const { companyId } = useCommunity()

  const { loading, data } = useQuery(GetRestrictedCompanySettingsDocument, {
    variables: { id: companyId ?? '' },
    skip: !companyId,
  })

  const { loading: loadingCommunities, data: communitiesData } = useQuery(GetPossiblePublicCommunitiesDocument, {
    skip: !companyId,
  })

  const communities =
    communitiesData?.communities?.edges?.map(edge => {
      return {
        communityId: edge?.node?.communityId,
        name: edge?.node?.name,
      } as CommunityRow
    }) ?? []
  const initialIds = data?.company?.authorizedCommunities?.edges?.map(edge => edge?.node?.communityId ?? '') ?? []

  if ((loading || loadingCommunities) && !data) {
    return <div>Loading...</div>
  }

  return (
    <CommunityAllowList
      companyId={companyId ?? ''}
      communities={communities}
      initialSelectedIds={initialIds}
      name={data?.company?.name ?? ''}
    />
  )
}
